import React, { FC, useCallback, useState } from 'react'
import * as Styled from './styled'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../misc'
import { StepForm, StepFormStep } from '../../form'
import { BackButton } from '../../buttons'
import { DuckFootMake } from './DuckFootMake'
import { DuckFootModels } from './DuckFootModels'
import { DuckFootHeader } from './DuckFootHeader'
import { ProductWizardResult } from './DuckFootResult'
import { AnalyticsPageEventInfo, AnalyticsTrackEventInfo, ProductWizardType, SubmitOptionsType } from '../types'
import { Currency } from '../../../globalTypes'
import { DuckFootStepNameForAnalytics, DuckFootWizardNode } from './types'

export enum ProductWizardDuckFootSteps {
  Make = 'wizard_make',
  Model = 'wizard_model',
  BattHeader = 'wizard_batt_header',
  Result = 'wizard_result',
}

type WizardPropsType = {
  opened: boolean
  onClose: () => void
  isMobile?: boolean
  productWizardData: DuckFootWizardNode | null
  handleProductId: (id: string) => void
  submit?: (product, options?: SubmitOptionsType<ProductWizardType.DuckFoot>) => void
  changeOrderList?: (id: string, qty: number, wizardComment: string) => void
  getPrettyPrice: (num: number | string, cur: Currency, priceType?: string) => string
  redirectToCart?: () => void
  product?: {
    id: string
    producer_id: string
    price?: string | null
    currency: Currency
    price_type: string
    min_qty?: number
    max_qty?: number
  }
  onSendAnalyticsPage?: (info: AnalyticsPageEventInfo) => void
  onSendAnalyticsTrack?: (info: AnalyticsTrackEventInfo) => void
}

export const DuckFoot: FC<WizardPropsType> = ({
  opened,
  onClose,
  isMobile,
  productWizardData,
  getPrettyPrice,
  redirectToCart,
  handleProductId,
  submit,
  changeOrderList,
  product,
  onSendAnalyticsPage,
  onSendAnalyticsTrack,
}: WizardPropsType) => {
  const { t } = useTranslation('productWizard')

  const [step, setStep] = useState(ProductWizardDuckFootSteps.Make)
  const [makeId, setMakeId] = useState('')
  const [makeName, setMakeName] = useState('')
  const [modelId, setModelId] = useState('')
  const [modelName, setModelName] = useState('')
  const [batt, setBatt] = useState('')
  const [headerName, setHeaderName] = useState('')
  const [qty, setQty] = useState<number | null>()
  const [recalculatedQty, setRecalculatedQty] = useState<number | null>(null)
  const [numberOfMachines, setNumberOfMachines] = useState<number>(1)
  const [productId, setProductId] = useState('')

  const getProducers = useCallback(() => {
    if (productWizardData?.children) {
      return productWizardData.children
    } else {
      return []
    }
  }, [productWizardData?.children])

  const getModels = useCallback(() => {
    const producers = getProducers()
    if (!producers?.length) return []
    const currentMake = producers.filter(item => {
      return item.id === makeId
    })
    return currentMake[0]?.children || []
  }, [getProducers, makeId])

  const getHeaders = useCallback(() => {
    const data = getProducers()
    if (!data?.length) return []
    const currentMake = data.filter(item => {
      return item.id === makeId
    })
    if (!currentMake[0]?.children) {
      return []
    } else {
      const currentModel = currentMake[0].children.filter(item => {
        return item.id === modelId
      })
      return currentModel[0]?.children || []
    }
  }, [getProducers, makeId, modelId])

  const handleWizardClose = useCallback(() => {
    onClose()
    setStep(ProductWizardDuckFootSteps.Make)
    setMakeId('')
    setMakeName('')
    setModelId('')
    setModelName('')
    setBatt('')
    setHeaderName('')
    setQty(null)
    setProductId('')
    setRecalculatedQty(null)
    setNumberOfMachines(1)
  }, [onClose])

  const handleStepMake = useCallback((id: string, name: string) => {
    setStep(ProductWizardDuckFootSteps.Model)
    setMakeId(id)
    setMakeName(name)
  }, [])

  const handleStepModel = useCallback(
    (modelId: string, modelName: string, productId) => {
      setStep(ProductWizardDuckFootSteps.BattHeader)
      setModelId(modelId)
      setModelName(modelName)
      setProductId(productId)
      handleProductId(productId)
    },
    [handleProductId],
  )

  const handleStepHeader = useCallback((id: string, name: string, qty: number | null) => {
    setStep(ProductWizardDuckFootSteps.Result)
    setHeaderName(name)
    setQty(qty)
    setRecalculatedQty(qty)
  }, [])

  const handleRecalculatedQty = useCallback((ratio: number, qty: number) => {
    setNumberOfMachines(ratio)
    setRecalculatedQty(ratio * qty)
  }, [])

  const handleAddToCart = useCallback(() => {
    const duckFootQty = recalculatedQty || 0
    const wizardComment = `${makeName} :: ${modelName} :: ${batt} :: ${headerName} :: ${recalculatedQty} ${t(
      'note.pcs',
    )} :: ${numberOfMachines} ${t('note.packs')}`
    handleWizardClose()
    submit &&
      submit(product, {
        qty: duckFootQty,
        packaging: null,
        wizard_comment: wizardComment,
      })
    submit && redirectToCart && redirectToCart()

    changeOrderList && changeOrderList(productId, duckFootQty, wizardComment)
  }, [
    recalculatedQty,
    handleWizardClose,
    submit,
    product,
    makeName,
    modelName,
    batt,
    headerName,
    t,
    numberOfMachines,
    redirectToCart,
    changeOrderList,
    productId,
  ])

  const mapStepNames = () => {
    switch (step) {
      case ProductWizardDuckFootSteps.Make:
        return DuckFootStepNameForAnalytics.ChooseMake
      case ProductWizardDuckFootSteps.Model:
        return DuckFootStepNameForAnalytics.ChooseModel
      case ProductWizardDuckFootSteps.BattHeader:
        return DuckFootStepNameForAnalytics.BattAndHeader
      case ProductWizardDuckFootSteps.Result:
        return DuckFootStepNameForAnalytics.Result
      default:
        return DuckFootStepNameForAnalytics.Unknown
    }
  }

  return (
    <Modal
      isOpen={opened}
      heading={t('title')}
      size={isMobile ? 'medium' : 'responsive'}
      onClose={() => {
        handleWizardClose()
        onSendAnalyticsTrack?.({ step: mapStepNames(), action: 'close' })
      }}
    >
      <Styled.StepFormWrapper>
        <StepForm step={step} completed={false} onTabClick={() => undefined} headerSize={'small'}>
          <StepFormStep id={ProductWizardDuckFootSteps.Make} title={t('stepForm.makeTitle')}>
            <Styled.ProductWizardMakeContainer>
              <DuckFootMake
                opened={opened}
                handleStepMake={handleStepMake}
                getProducers={getProducers}
                onSendAnalyticsPage={onSendAnalyticsPage}
                onSendAnalyticsTrack={onSendAnalyticsTrack}
              />
            </Styled.ProductWizardMakeContainer>
          </StepFormStep>
          <StepFormStep id={ProductWizardDuckFootSteps.Model} title={t('stepForm.modelTitle')}>
            <Styled.StepFormStepWizardModelsWrapper>
              <Styled.ProductWizardModelsContainer>
                <DuckFootModels
                  handleStepModel={handleStepModel}
                  getModels={getModels}
                  onSendAnalyticsPage={onSendAnalyticsPage}
                  onSendAnalyticsTrack={onSendAnalyticsTrack}
                />
              </Styled.ProductWizardModelsContainer>
              <Styled.ProductWizardModelsButtonWrapper>
                <BackButton
                  data-test-id={ProductWizardDuckFootSteps.Model}
                  onClick={() => {
                    setStep(ProductWizardDuckFootSteps.Make)
                    onSendAnalyticsTrack?.({ step: DuckFootStepNameForAnalytics.ChooseModel, action: 'back' })
                  }}
                  size={'small'}
                />
              </Styled.ProductWizardModelsButtonWrapper>
            </Styled.StepFormStepWizardModelsWrapper>
          </StepFormStep>
          <StepFormStep id={ProductWizardDuckFootSteps.BattHeader} title={t('stepForm.battAndHeaderTitle')}>
            <DuckFootHeader
              handleStepHeader={handleStepHeader}
              setBatt={setBatt}
              getHeaders={getHeaders}
              onSendAnalyticsPage={onSendAnalyticsPage}
              onSendAnalyticsTrack={onSendAnalyticsTrack}
            />
            <Styled.ProductWizardHeaderButtonWrapper>
              <BackButton
                data-test-id={ProductWizardDuckFootSteps.BattHeader}
                onClick={() => {
                  setStep(ProductWizardDuckFootSteps.Model)
                  onSendAnalyticsTrack?.({ step: DuckFootStepNameForAnalytics.BattAndHeader, action: 'back' })
                }}
                size={'small'}
              />
            </Styled.ProductWizardHeaderButtonWrapper>
          </StepFormStep>
          <StepFormStep id={ProductWizardDuckFootSteps.Result} title={t('stepForm.resultTitle')}>
            <Styled.ProductWizardResultContainer>
              <ProductWizardResult
                qty={qty || 0}
                makeName={makeName}
                modelName={modelName}
                headerName={headerName}
                batt={batt}
                onAddToCart={handleAddToCart}
                getPrettyPrice={getPrettyPrice}
                price={product?.price}
                currency={product?.currency}
                priceType={product?.price_type}
                minQty={product?.min_qty}
                maxQty={product?.max_qty && qty ? Math.floor(product?.max_qty / qty) : undefined}
                handleRecalculatedQty={handleRecalculatedQty}
                recalculatedQty={recalculatedQty}
                numberOfMachines={numberOfMachines}
                onSendAnalyticsPage={onSendAnalyticsPage}
                onSendAnalyticsTrack={onSendAnalyticsTrack}
              />
              <Styled.ResultButtonsContainer>
                <BackButton
                  data-test-id={ProductWizardDuckFootSteps.Result}
                  onClick={() => {
                    setStep(ProductWizardDuckFootSteps.BattHeader)
                    onSendAnalyticsTrack?.({ step: DuckFootStepNameForAnalytics.Result, action: 'back' })
                  }}
                  size={'small'}
                />
              </Styled.ResultButtonsContainer>
            </Styled.ProductWizardResultContainer>
          </StepFormStep>
        </StepForm>
      </Styled.StepFormWrapper>
    </Modal>
  )
}
